import { Sha256 } from "@aws-crypto/sha256-browser";

import {
  containsUnsafeCustomerText,
  isAllowedNucleusStreamUrl,
  isNucleusTurnOutcome,
  isValidNucleusTurnOutcomeStatus,
  nucleusTurnOutcomeFinishReason,
  type CommonScopeIntent,
  type NucleusReconciliationStatus,
  type NucleusTraceStatus,
  type NucleusTurnOutcome,
} from "./nucleus_sse_client";

export type NucleusConversationRunRef = `sha256:${string}`;
export type NucleusConversationRunScope = CommonScopeIntent["run"];

export type NucleusConversationScope = {
  organizationId: string;
  runRef: NucleusConversationRunRef | null;
};

// Legacy responses carry the exact run binding. Keep that migration-only
// material outside the enumerable customer-facing object so it cannot be
// rendered or serialized by a consumer of the parsed conversation.
const legacyRunByScope = new WeakMap<
  NucleusConversationScope,
  NucleusConversationRunScope | null
>();
const expectedRunByScope = new WeakMap<
  NucleusConversationScope,
  NucleusConversationRunScope | null
>();

export type CustomerEvidenceReference = {
  version: "CustomerEvidenceReference.v1";
  id: string;
  kind: "documentation" | "run_data" | "lineage";
  title: string;
  summary?: string;
  document?: {
    title: string;
    section?: string;
    excerpt?: string;
    freshness?: string;
  };
};

export type NucleusConversationMessage = {
  version: "CommonConversationMessage.v1";
  id: string;
  turnId?: string;
  role: "user" | "assistant";
  text: string;
  createdAt: string;
  status: "complete" | "unsupported" | "failed" | "cancelled";
  answerStatus?: {
    traceStatus: NucleusTraceStatus;
    reconciliationStatus: NucleusReconciliationStatus;
    outcome?: NucleusTurnOutcome;
  };
  sources?: CustomerEvidenceReference[];
};

export type NucleusConversationSummary = {
  version: "CommonConversationSummary.v1";
  id: string;
  title: string;
  scope: NucleusConversationScope;
  state: "idle" | "running" | "failed";
  createdAt: string;
  updatedAt: string;
  preview?: string;
  canContinue: boolean;
};

export type NucleusConversationDetail = NucleusConversationSummary & {
  messages: NucleusConversationMessage[];
  activeContext: null;
};

/**
 * One newest-first transcript page. Messages within the page remain ordered
 * oldest-to-newest for direct rendering; nextCursor requests the next older
 * page and is never interpreted by the browser.
 */
export type NucleusConversationDetailPage = {
  conversation: NucleusConversationDetail;
  nextCursor?: string;
};

export type NucleusConversationClientErrorCode =
  | "DESTINATION_NOT_ALLOWED"
  | "INVALID_REQUEST"
  | "INVALID_RESPONSE"
  | "SCOPE_MISMATCH"
  | "UNAVAILABLE";

export class NucleusConversationClientError extends Error {
  constructor(
    readonly code: NucleusConversationClientErrorCode,
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "NucleusConversationClientError";
  }
}

const MAX_RESPONSE_CHARS = 512 * 1024;
const MAX_CONVERSATIONS = 50;
const MAX_MESSAGES = 200;
const MAX_SOURCES = 25;
const MAX_PREVIEW_CHARS = 500;
const MAX_PERSISTED_TEXT_CHARS = 40_000;
const IDENTIFIER_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]*$/;
const ORGANIZATION_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;
const RUN_REF_PATTERN = /^sha256:[0-9a-f]{64}$/;
const ISO_TIMESTAMP_PATTERN =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|[+-]\d{2}:\d{2})$/;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const hasOnlyKeys = (
  value: Record<string, unknown>,
  allowedKeys: readonly string[],
): boolean => Object.keys(value).every((key) => allowedKeys.includes(key));

const isBoundedString = (value: unknown, maxLength: number): value is string =>
  typeof value === "string" && value.length > 0 && value.length <= maxLength;

const isBoundedUtf8String = (
  value: unknown,
  maxBytes: number,
): value is string => {
  if (
    typeof value !== "string" ||
    value.length === 0 ||
    value.length > maxBytes
  ) {
    return false;
  }

  let byteLength = 0;
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit <= 0x7f) {
      byteLength += 1;
    } else if (codeUnit <= 0x7ff) {
      byteLength += 2;
    } else if (
      codeUnit >= 0xd800 &&
      codeUnit <= 0xdbff &&
      index + 1 < value.length &&
      value.charCodeAt(index + 1) >= 0xdc00 &&
      value.charCodeAt(index + 1) <= 0xdfff
    ) {
      byteLength += 4;
      index += 1;
    } else {
      byteLength += 3;
    }
    if (byteLength > maxBytes) return false;
  }
  return true;
};

const isIdentifier = (value: unknown): value is string =>
  isBoundedString(value, 128) && IDENTIFIER_PATTERN.test(value);

const isIsoTimestamp = (value: unknown): value is string =>
  isBoundedString(value, 64) &&
  ISO_TIMESTAMP_PATTERN.test(value) &&
  Number.isFinite(Date.parse(value));

function parseRunScope(value: unknown): CommonScopeIntent["run"] | undefined {
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, [
      "inputId",
      "workflowId",
      "settingsNameAndVersion",
      "runAttemptId",
    ]) ||
    !isBoundedString(value.inputId, 256) ||
    !isBoundedString(value.workflowId, 256) ||
    !isBoundedString(value.settingsNameAndVersion, 256) ||
    !isBoundedString(value.runAttemptId, 256)
  ) {
    return undefined;
  }
  return {
    inputId: value.inputId,
    workflowId: value.workflowId,
    settingsNameAndVersion: value.settingsNameAndVersion,
    runAttemptId: value.runAttemptId,
  };
}

function parseScopeIntent(value: unknown): CommonScopeIntent | undefined {
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, ["version", "surfacePath", "run"]) ||
    value.version !== "CommonScopeIntent.v1" ||
    !isBoundedString(value.surfacePath, 256) ||
    !value.surfacePath.startsWith("/") ||
    value.surfacePath.includes("?") ||
    value.surfacePath.includes("#")
  ) {
    return undefined;
  }
  const run = parseRunScope(value.run);
  if (!run) return undefined;
  return {
    version: "CommonScopeIntent.v1",
    surfacePath: value.surfacePath,
    run,
  };
}

function parseScope(value: unknown): NucleusConversationScope | undefined {
  if (
    !isRecord(value) ||
    !isBoundedString(value.organizationId, 128) ||
    !ORGANIZATION_PATTERN.test(value.organizationId)
  ) {
    return undefined;
  }

  if (
    Object.prototype.hasOwnProperty.call(value, "runRef") &&
    hasOnlyKeys(value, ["organizationId", "runRef"]) &&
    (value.runRef === null ||
      (typeof value.runRef === "string" && RUN_REF_PATTERN.test(value.runRef)))
  ) {
    return {
      organizationId: value.organizationId,
      runRef: value.runRef as NucleusConversationRunRef | null,
    };
  }

  if (
    !Object.prototype.hasOwnProperty.call(value, "run") ||
    !hasOnlyKeys(value, ["organizationId", "run"])
  ) {
    return undefined;
  }
  const legacyRun = value.run === null ? null : parseRunScope(value.run);
  if (legacyRun === undefined) return undefined;
  const scope: NucleusConversationScope = {
    organizationId: value.organizationId,
    runRef: null,
  };
  legacyRunByScope.set(scope, legacyRun);
  return scope;
}

export async function nucleusConversationRunRef(
  run: CommonScopeIntent["run"] | null,
): Promise<NucleusConversationRunRef | null> {
  if (run === null) return null;
  const parsed = parseRunScope(run);
  if (!parsed) {
    throw new NucleusConversationClientError(
      "INVALID_REQUEST",
      "Nucleus conversation scope is unavailable.",
    );
  }
  const material = JSON.stringify([
    "CommonRunBinding.v1",
    parsed.inputId,
    parsed.workflowId,
    parsed.settingsNameAndVersion,
    parsed.runAttemptId,
  ]);
  const hash = new Sha256();
  hash.update(material, "utf8");
  const digest = await hash.digest();
  const hex = Array.from(digest, (value) =>
    value.toString(16).padStart(2, "0"),
  ).join("");
  return `sha256:${hex}`;
}

const runsMatch = (
  left: NucleusConversationRunScope | null,
  right: NucleusConversationRunScope | null,
): boolean =>
  left === null || right === null
    ? left === right
    : left.inputId === right.inputId &&
      left.workflowId === right.workflowId &&
      left.settingsNameAndVersion === right.settingsNameAndVersion &&
      left.runAttemptId === right.runAttemptId;

export async function nucleusConversationScopeForRun(
  organizationId: string,
  run: NucleusConversationRunScope | null,
): Promise<NucleusConversationScope> {
  const parsedRun = run === null ? null : parseRunScope(run);
  const scope = parseScope({
    organizationId,
    runRef: await nucleusConversationRunRef(run),
  });
  if (!scope || parsedRun === undefined) {
    throw new NucleusConversationClientError(
      "INVALID_REQUEST",
      "Nucleus conversation scope is unavailable.",
    );
  }
  expectedRunByScope.set(scope, parsedRun);
  return scope;
}

export function nucleusConversationScopesMatch(
  actual: NucleusConversationScope,
  expected: NucleusConversationScope,
): boolean {
  if (actual.organizationId !== expected.organizationId) return false;

  const legacyRun = legacyRunByScope.get(actual);
  if (legacyRun !== undefined) {
    const expectedRun = expectedRunByScope.has(expected)
      ? expectedRunByScope.get(expected)
      : legacyRunByScope.get(expected);
    if (expectedRun !== undefined) return runsMatch(legacyRun, expectedRun);
    return legacyRun === null && expected.runRef === null;
  }
  return actual.runRef === expected.runRef;
}

export function parseCustomerEvidenceReference(
  value: unknown,
): CustomerEvidenceReference | undefined {
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, [
      "version",
      "id",
      "kind",
      "title",
      "summary",
      "document",
    ]) ||
    value.version !== "CustomerEvidenceReference.v1" ||
    !isIdentifier(value.id) ||
    (value.kind !== "documentation" &&
      value.kind !== "run_data" &&
      value.kind !== "lineage") ||
    !isBoundedUtf8String(value.title, 256) ||
    (value.summary !== undefined && !isBoundedString(value.summary, 1_000))
  ) {
    return undefined;
  }

  let document: CustomerEvidenceReference["document"];
  if (value.document !== undefined) {
    if (
      !isRecord(value.document) ||
      !hasOnlyKeys(value.document, [
        "title",
        "section",
        "excerpt",
        "freshness",
      ]) ||
      !isBoundedUtf8String(value.document.title, 256) ||
      (value.document.section !== undefined &&
        !isBoundedString(value.document.section, 200)) ||
      (value.document.excerpt !== undefined &&
        !isBoundedString(value.document.excerpt, 2_000)) ||
      (value.document.freshness !== undefined &&
        !isBoundedString(value.document.freshness, 120))
    ) {
      return undefined;
    }
    document = {
      title: value.document.title,
      ...(typeof value.document.section === "string"
        ? { section: value.document.section }
        : {}),
      ...(typeof value.document.excerpt === "string"
        ? { excerpt: value.document.excerpt }
        : {}),
      ...(typeof value.document.freshness === "string"
        ? { freshness: value.document.freshness }
        : {}),
    };
  }

  return {
    version: "CustomerEvidenceReference.v1",
    id: value.id,
    kind: value.kind,
    title: value.title,
    ...(typeof value.summary === "string" ? { summary: value.summary } : {}),
    ...(document ? { document } : {}),
  };
}

function parseMessage(value: unknown): NucleusConversationMessage | undefined {
  const emptyTerminalAssistant =
    isRecord(value) &&
    value.role === "assistant" &&
    (value.status === "failed" || value.status === "cancelled") &&
    value.text === "";
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, [
      "version",
      "id",
      "turnId",
      "role",
      "text",
      "createdAt",
      "status",
      "answerStatus",
      "sources",
    ]) ||
    value.version !== "CommonConversationMessage.v1" ||
    !isIdentifier(value.id) ||
    (value.turnId !== undefined && !isIdentifier(value.turnId)) ||
    (value.role !== "user" && value.role !== "assistant") ||
    (!isBoundedString(value.text, MAX_PERSISTED_TEXT_CHARS) &&
      !emptyTerminalAssistant) ||
    !isIsoTimestamp(value.createdAt) ||
    (value.status !== "complete" &&
      value.status !== "unsupported" &&
      value.status !== "failed" &&
      value.status !== "cancelled")
  ) {
    return undefined;
  }
  if (typeof value.text !== "string") return undefined;

  let answerStatus: NucleusConversationMessage["answerStatus"];
  if (value.answerStatus !== undefined) {
    if (
      !isRecord(value.answerStatus) ||
      !hasOnlyKeys(value.answerStatus, [
        "traceStatus",
        "reconciliationStatus",
        "outcome",
      ]) ||
      (value.answerStatus.traceStatus !== "full_trace" &&
        value.answerStatus.traceStatus !== "partial" &&
        value.answerStatus.traceStatus !== "unsupported" &&
        value.answerStatus.traceStatus !== "not_applicable") ||
      (value.answerStatus.reconciliationStatus !== "matched" &&
        value.answerStatus.reconciliationStatus !== "partial" &&
        value.answerStatus.reconciliationStatus !== "unsupported" &&
        value.answerStatus.reconciliationStatus !== "not_applicable") ||
      !(
        (value.answerStatus.traceStatus === "full_trace" &&
          value.answerStatus.reconciliationStatus === "matched") ||
        (value.answerStatus.traceStatus === "partial" &&
          (value.answerStatus.reconciliationStatus === "matched" ||
            value.answerStatus.reconciliationStatus === "partial")) ||
        (value.answerStatus.traceStatus === "unsupported" &&
          value.answerStatus.reconciliationStatus === "unsupported") ||
        (value.answerStatus.traceStatus === "not_applicable" &&
          value.answerStatus.reconciliationStatus === "not_applicable")
      ) ||
      (value.answerStatus.outcome !== undefined &&
        (!isNucleusTurnOutcome(value.answerStatus.outcome) ||
          !isValidNucleusTurnOutcomeStatus({
            outcome: value.answerStatus.outcome,
            status: value.answerStatus.traceStatus,
            reconciliationStatus: value.answerStatus.reconciliationStatus,
          })))
    ) {
      return undefined;
    }
    answerStatus = {
      traceStatus: value.answerStatus.traceStatus,
      reconciliationStatus: value.answerStatus.reconciliationStatus,
      ...(isNucleusTurnOutcome(value.answerStatus.outcome)
        ? { outcome: value.answerStatus.outcome }
        : {}),
    };
  }

  let sources: CustomerEvidenceReference[] | undefined;
  if (value.sources !== undefined) {
    if (!Array.isArray(value.sources) || value.sources.length > MAX_SOURCES) {
      return undefined;
    }
    sources = [];
    for (const rawSource of value.sources) {
      const source = parseCustomerEvidenceReference(rawSource);
      if (!source) return undefined;
      sources.push(source);
    }
  }
  if (
    answerStatus?.outcome !== undefined &&
    (value.role !== "assistant" ||
      value.status !==
        (nucleusTurnOutcomeFinishReason(answerStatus.outcome) === "completed"
          ? "complete"
          : "unsupported") ||
      (answerStatus.outcome === "answered_grounded" ||
        answerStatus.outcome === "partial_grounded") !==
        Boolean(sources && sources.length > 0))
  ) {
    return undefined;
  }
  if (
    answerStatus?.traceStatus === "not_applicable" &&
    (value.role !== "assistant" ||
      value.status !== "complete" ||
      (sources !== undefined && sources.length > 0))
  ) {
    return undefined;
  }

  return {
    version: "CommonConversationMessage.v1",
    id: value.id,
    ...(typeof value.turnId === "string" ? { turnId: value.turnId } : {}),
    role: value.role,
    text: value.text,
    createdAt: value.createdAt,
    status: value.status,
    ...(answerStatus ? { answerStatus } : {}),
    ...(sources ? { sources } : {}),
  };
}

function parseSummary(
  value: unknown,
  allowDetailFields = false,
): NucleusConversationSummary | undefined {
  const allowedKeys = [
    "version",
    "id",
    "title",
    "scope",
    "state",
    "createdAt",
    "updatedAt",
    "preview",
    "canContinue",
    ...(allowDetailFields ? ["messages", "activeContext"] : []),
  ];
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, allowedKeys) ||
    value.version !== "CommonConversationSummary.v1" ||
    !isIdentifier(value.id) ||
    !isBoundedString(value.title, 160) ||
    (value.state !== "idle" &&
      value.state !== "running" &&
      value.state !== "failed") ||
    !isIsoTimestamp(value.createdAt) ||
    !isIsoTimestamp(value.updatedAt) ||
    (value.preview !== undefined &&
      !isBoundedString(value.preview, MAX_PERSISTED_TEXT_CHARS)) ||
    typeof value.canContinue !== "boolean"
  ) {
    return undefined;
  }
  const scope = parseScope(value.scope);
  if (!scope) return undefined;
  return {
    version: "CommonConversationSummary.v1",
    id: value.id,
    title: value.title,
    scope,
    state: value.state,
    createdAt: value.createdAt,
    updatedAt: value.updatedAt,
    ...(typeof value.preview === "string"
      ? { preview: value.preview.slice(0, MAX_PREVIEW_CHARS) }
      : {}),
    canContinue: value.canContinue,
  };
}

function parseDetail(value: unknown): NucleusConversationDetail | undefined {
  const summary = parseSummary(value, true);
  if (
    !summary ||
    !isRecord(value) ||
    !Array.isArray(value.messages) ||
    value.messages.length > MAX_MESSAGES ||
    value.activeContext !== null
  ) {
    return undefined;
  }
  const messages: NucleusConversationMessage[] = [];
  for (const rawMessage of value.messages) {
    const message = parseMessage(rawMessage);
    if (!message) return undefined;
    const sourceText = (message.sources ?? [])
      .flatMap((source) => [
        source.title,
        source.summary ?? "",
        source.document?.title ?? "",
        source.document?.section ?? "",
        source.document?.excerpt ?? "",
        source.document?.freshness ?? "",
      ])
      .join("\n");
    if (
      message.role === "assistant" &&
      ((message.text &&
        containsUnsafeCustomerText(
          message.text,
          summary.scope.organizationId,
        )) ||
        (sourceText &&
          containsUnsafeCustomerText(sourceText, summary.scope.organizationId)))
    ) {
      return undefined;
    }
    messages.push(message);
  }
  return { ...summary, messages, activeContext: null };
}

function assertExpectedScope(
  actual: NucleusConversationScope,
  expected: NucleusConversationScope,
): void {
  if (!nucleusConversationScopesMatch(actual, expected)) {
    throw new NucleusConversationClientError(
      "SCOPE_MISMATCH",
      "Nucleus returned a conversation outside the active customer and run.",
    );
  }
}

function apiUrl(streamUrl: string, path: string): URL {
  if (!isAllowedNucleusStreamUrl(streamUrl)) {
    throw new NucleusConversationClientError(
      "DESTINATION_NOT_ALLOWED",
      "Nucleus destination is not allowed.",
    );
  }
  const url = new URL(streamUrl);
  url.pathname = path;
  url.search = "";
  url.hash = "";
  return url;
}

async function responseJson(response: Response): Promise<unknown> {
  if (!response.ok) {
    throw new NucleusConversationClientError(
      "UNAVAILABLE",
      `Nucleus conversation history is unavailable (HTTP ${response.status}).`,
      response.status,
    );
  }
  if (!response.headers.get("content-type")?.includes("application/json")) {
    throw new NucleusConversationClientError(
      "INVALID_RESPONSE",
      "Nucleus returned an invalid conversation response.",
    );
  }
  const responseText = await response.text();
  if (responseText.length > MAX_RESPONSE_CHARS) {
    throw new NucleusConversationClientError(
      "INVALID_RESPONSE",
      "Nucleus returned an invalid conversation response.",
    );
  }
  try {
    return JSON.parse(responseText);
  } catch {
    throw new NucleusConversationClientError(
      "INVALID_RESPONSE",
      "Nucleus returned an invalid conversation response.",
    );
  }
}

const requestHeaders = (token: string): Record<string, string> => {
  if (
    !isBoundedString(token, 16 * 1024) ||
    token.trim() !== token ||
    /[\r\n]/.test(token)
  ) {
    throw new NucleusConversationClientError(
      "INVALID_REQUEST",
      "Nucleus conversation request is invalid.",
    );
  }
  return {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };
};

function detailFromEnvelope(
  candidate: unknown,
  expectedScope: NucleusConversationScope,
): NucleusConversationDetail {
  if (
    !isRecord(candidate) ||
    !hasOnlyKeys(candidate, ["version", "conversation"]) ||
    candidate.version !== "CommonConversationDetailResponse.v1"
  ) {
    throw new NucleusConversationClientError(
      "INVALID_RESPONSE",
      "Nucleus returned an invalid conversation response.",
    );
  }
  const conversation = parseDetail(candidate.conversation);
  if (!conversation) {
    throw new NucleusConversationClientError(
      "INVALID_RESPONSE",
      "Nucleus returned an invalid conversation response.",
    );
  }
  assertExpectedScope(conversation.scope, expectedScope);
  return conversation;
}

export async function createNucleusConversation({
  streamUrl,
  token,
  organizationId,
  scopeIntent,
  title,
  signal,
  fetchImpl = fetch,
}: {
  streamUrl: string;
  token: string;
  organizationId: string;
  scopeIntent?: CommonScopeIntent;
  title?: string;
  signal: AbortSignal;
  fetchImpl?: typeof fetch;
}): Promise<NucleusConversationDetail> {
  const parsedScopeIntent =
    scopeIntent === undefined ? undefined : parseScopeIntent(scopeIntent);
  const expectedRun = parsedScopeIntent?.run ?? null;
  const expectedScope = await nucleusConversationScopeForRun(
    organizationId,
    expectedRun,
  ).catch(() => undefined);
  if (
    !expectedScope ||
    (scopeIntent !== undefined && !parsedScopeIntent) ||
    (title !== undefined && !isBoundedString(title.trim(), 160))
  ) {
    throw new NucleusConversationClientError(
      "INVALID_REQUEST",
      "Nucleus conversation request is invalid.",
    );
  }
  const body = {
    version: "CommonConversationCreateRequest.v1" as const,
    organizationId,
    ...(parsedScopeIntent ? { scopeIntent: parsedScopeIntent } : {}),
    ...(title ? { title: title.trim() } : {}),
  };
  const response = await fetchImpl(
    apiUrl(streamUrl, "/nucleusapi/conversations").toString(),
    {
      method: "POST",
      redirect: "error",
      headers: {
        ...requestHeaders(token),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
      signal,
    },
  );
  return detailFromEnvelope(await responseJson(response), expectedScope);
}

export async function listNucleusConversations({
  streamUrl,
  token,
  organizationId,
  limit = 20,
  runRef,
  cursor,
  signal,
  fetchImpl = fetch,
}: {
  streamUrl: string;
  token: string;
  organizationId: string;
  limit?: number;
  runRef?: NucleusConversationRunRef;
  cursor?: string;
  signal: AbortSignal;
  fetchImpl?: typeof fetch;
}): Promise<{
  conversations: NucleusConversationSummary[];
  nextCursor?: string;
}> {
  const expectedScope = parseScope({ organizationId, runRef: null });
  if (
    !expectedScope ||
    !Number.isInteger(limit) ||
    limit < 1 ||
    limit > MAX_CONVERSATIONS ||
    (runRef !== undefined && !RUN_REF_PATTERN.test(runRef)) ||
    (cursor !== undefined && !isBoundedString(cursor, 2_000))
  ) {
    throw new NucleusConversationClientError(
      "INVALID_REQUEST",
      "Nucleus conversation request is invalid.",
    );
  }
  const url = apiUrl(streamUrl, "/nucleusapi/conversations");
  url.searchParams.set("organizationId", organizationId);
  url.searchParams.set("limit", String(limit));
  if (runRef) url.searchParams.set("runRef", runRef);
  if (cursor) url.searchParams.set("cursor", cursor);
  const response = await fetchImpl(url.toString(), {
    method: "GET",
    redirect: "error",
    headers: requestHeaders(token),
    signal,
  });
  const candidate = await responseJson(response);
  if (
    !isRecord(candidate) ||
    !hasOnlyKeys(candidate, ["version", "conversations", "nextCursor"]) ||
    candidate.version !== "CommonConversationListResponse.v1" ||
    !Array.isArray(candidate.conversations) ||
    candidate.conversations.length > MAX_CONVERSATIONS ||
    (candidate.nextCursor !== undefined &&
      !isBoundedString(candidate.nextCursor, 2_000))
  ) {
    throw new NucleusConversationClientError(
      "INVALID_RESPONSE",
      "Nucleus returned an invalid conversation response.",
    );
  }
  const conversations: NucleusConversationSummary[] = [];
  for (const rawConversation of candidate.conversations) {
    const conversation = parseSummary(rawConversation);
    if (!conversation) {
      throw new NucleusConversationClientError(
        "INVALID_RESPONSE",
        "Nucleus returned an invalid conversation response.",
      );
    }
    if (conversation.scope.organizationId !== organizationId) {
      throw new NucleusConversationClientError(
        "SCOPE_MISMATCH",
        "Nucleus returned a conversation outside the active customer.",
      );
    }
    conversations.push(conversation);
  }
  return {
    conversations,
    ...(typeof candidate.nextCursor === "string"
      ? { nextCursor: candidate.nextCursor }
      : {}),
  };
}

export async function getNucleusConversationPage({
  streamUrl,
  token,
  conversationId,
  expectedScope,
  limit = MAX_MESSAGES,
  cursor,
  signal,
  fetchImpl = fetch,
}: {
  streamUrl: string;
  token: string;
  conversationId: string;
  expectedScope: NucleusConversationScope;
  limit?: number;
  cursor?: string;
  signal: AbortSignal;
  fetchImpl?: typeof fetch;
}): Promise<NucleusConversationDetailPage> {
  if (
    !isIdentifier(conversationId) ||
    !parseScope(expectedScope) ||
    !Number.isInteger(limit) ||
    limit < 1 ||
    limit > MAX_MESSAGES ||
    (cursor !== undefined && !isBoundedString(cursor, 2_000))
  ) {
    throw new NucleusConversationClientError(
      "INVALID_REQUEST",
      "Nucleus conversation request is invalid.",
    );
  }
  const url = apiUrl(
    streamUrl,
    `/nucleusapi/conversations/${encodeURIComponent(conversationId)}`,
  );
  url.searchParams.set("organizationId", expectedScope.organizationId);
  url.searchParams.set("limit", String(limit));
  if (cursor) url.searchParams.set("cursor", cursor);
  const response = await fetchImpl(url.toString(), {
    method: "GET",
    redirect: "error",
    headers: requestHeaders(token),
    signal,
  });
  const candidate = await responseJson(response);
  if (
    isRecord(candidate) &&
    candidate.version === "CommonConversationDetailResponse.v1"
  ) {
    return {
      conversation: detailFromEnvelope(candidate, expectedScope),
    };
  }
  if (
    !isRecord(candidate) ||
    !hasOnlyKeys(candidate, ["version", "conversation", "nextCursor"]) ||
    candidate.version !== "CommonConversationDetailResponse.v2" ||
    (candidate.nextCursor !== undefined &&
      !isBoundedString(candidate.nextCursor, 2_000))
  ) {
    throw new NucleusConversationClientError(
      "INVALID_RESPONSE",
      "Nucleus returned an invalid conversation response.",
    );
  }
  const conversation = parseDetail(candidate.conversation);
  if (!conversation) {
    throw new NucleusConversationClientError(
      "INVALID_RESPONSE",
      "Nucleus returned an invalid conversation response.",
    );
  }
  assertExpectedScope(conversation.scope, expectedScope);
  return {
    conversation,
    ...(typeof candidate.nextCursor === "string"
      ? { nextCursor: candidate.nextCursor }
      : {}),
  };
}

export async function getNucleusConversation(
  input: Parameters<typeof getNucleusConversationPage>[0],
): Promise<NucleusConversationDetail> {
  return (await getNucleusConversationPage(input)).conversation;
}
